import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export default function CategoryDetailPanel({ group, onClose, onAgentSelect, onAddAgent, onEditAgent, onSettingsClick }) {
  const [agents, setAgents] = useState(group.agents)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [openMenuIndex, setOpenMenuIndex] = useState(null)
  const menuRef = useRef(null)

  useEffect(() => {
    if (openMenuIndex === null) return
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuIndex(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openMenuIndex])

  return (
    <motion.div
      layout
      initial={{ width: 0, opacity: 0 }}
      animate={{ width: 260, opacity: 1 }}
      exit={{ width: 0, opacity: 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1], layout: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }}
      style={{
        height: '100%',
        background: 'transparent',
        borderRadius: 4,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25, duration: 0.3 }}
        style={{ padding: 16, display: 'flex', flexDirection: 'column', height: '100%', gap: 16, boxSizing: 'border-box' }}
      >
        {/* Title */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#FFFFFF', flex: 1 }}>{group.type}</span>
          {group.type === 'Purchase Orders' && (
            <motion.button
              onClick={() => onSettingsClick?.()}
              whileHover={{ scale: 1.15, color: 'rgba(255,255,255,0.9)' }}
              whileTap={{ scale: 0.9 }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
              <SettingsIcon />
            </motion.button>
          )}
          <motion.button
            onClick={onClose}
            whileHover={{ scale: 1.15, color: 'rgba(255,255,255,0.9)' }}
            whileTap={{ scale: 0.9 }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.45)', padding: 0, display: 'flex', alignItems: 'center', flexShrink: 0 }}
          >
            <BackArrowIcon />
          </motion.button>
        </div>

        {/* Agent List header */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={labelStyle}>Agent List</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.08)', borderRadius: 10, padding: '2px 7px' }}>
            {agents.length}
          </span>
        </div>

        {/* Agent list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, overflowY: 'auto' }}>
          {agents.map((agent, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              onClick={() => { setSelectedIndex(i); onAgentSelect?.(agent.name) }}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 6px 8px 10px',
                paddingLeft: i === 0 ? 10 : 18,
                background: selectedIndex === i ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                borderRadius: 6,
                borderLeft: selectedIndex === i ? '2px solid rgba(96,165,250,0.85)' : '2px solid transparent',
                cursor: 'pointer',
              }}
            >
              {i > 0 && <ChildArrowIcon />}
              <div style={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                background: agent.attention ? '#EB1700' : '#4ADE80',
              }} />
              <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>
                {agent.name}
              </span>
              {/* 3-dot menu button */}
              <motion.button
                onClick={(e) => { e.stopPropagation(); setOpenMenuIndex(openMenuIndex === i ? null : i) }}
                whileHover={{ background: 'rgba(255,255,255,0.12)' }}
                whileTap={{ scale: 0.9 }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.35)', padding: '2px 4px', borderRadius: 4, display: 'flex', alignItems: 'center', flexShrink: 0 }}
              >
                <DotsIcon />
              </motion.button>
              {/* Dropdown */}
              <AnimatePresence>
                {openMenuIndex === i && (
                  <motion.div
                    ref={menuRef}
                    initial={{ opacity: 0, scale: 0.92, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: -4 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 4,
                      zIndex: 50,
                      background: 'rgba(30,30,30,0.97)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 8,
                      overflow: 'hidden',
                      minWidth: 130,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                    }}
                  >
                    {[
                      { label: 'Edit', icon: <EditIcon /> },
                      { label: 'Refresh', icon: <RefreshIcon /> },
                      { label: 'Delete', icon: <DeleteIcon />, danger: true },
                    ].map(({ label, icon, danger }) => (
                      <motion.button
                        key={label}
                        onClick={() => {
                          if (label === 'Edit') {
                            onEditAgent?.(agent)
                          } else if (label === 'Delete') {
                            const next = agents.filter((_, idx) => idx !== i)
                            setAgents(next)
                            if (selectedIndex === i) {
                              setSelectedIndex(0)
                              onAgentSelect?.(next[0]?.name ?? null)
                            } else if (selectedIndex > i) {
                              setSelectedIndex(selectedIndex - 1)
                            }
                          }
                          setOpenMenuIndex(null)
                        }}
                        whileHover={{ background: danger ? 'rgba(235,23,0,0.15)' : 'rgba(255,255,255,0.08)' }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          width: '100%', padding: '9px 12px',
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: danger ? 'rgba(235,100,80,0.9)' : 'rgba(255,255,255,0.75)',
                          fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
                          textAlign: 'left',
                        }}
                      >
                        {icon}
                        {label}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}

        </div>

        {/* Add agent */}
        <motion.button
            onClick={() => onAddAgent?.()}
            whileHover={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.35)' }}
            whileTap={{ scale: 0.97 }}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px dashed rgba(255,255,255,0.2)',
              borderRadius: 6,
              padding: '9px',
              color: 'rgba(255,255,255,0.4)',
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              width: '100%',
              flexShrink: 0,
            }}
          >
            + Add Agent
          </motion.button>
      </motion.div>
    </motion.div>
  )
}

function ChildArrowIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2 1v5a1 1 0 001 1h5" stroke="rgba(255,255,255,0.25)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.5 5.5L8 7l-1.5 1.5" stroke="rgba(255,255,255,0.25)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <circle cx="4.5" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="11.5" cy="8" r="2" stroke="currentColor" strokeWidth="1.3" />
      <line x1="6.5" y1="8" x2="9.5" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="1" y1="8" x2="2.5" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="13.5" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function BackArrowIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function DotsIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <circle cx="6" cy="2.5" r="1" fill="currentColor" />
      <circle cx="6" cy="6" r="1" fill="currentColor" />
      <circle cx="6" cy="9.5" r="1" fill="currentColor" />
    </svg>
  )
}

function EditIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7.5 1.5l2 2L3 10H1V8L7.5 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  )
}

function RefreshIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{ flexShrink: 0 }}>
      <path d="M9.5 5.5A4 4 0 115.5 1.5h2M7.5 1.5l2 1-1 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" style={{ flexShrink: 0 }}>
      <path d="M1.5 3h8M4 3V2h3v1M2.5 3l.5 6.5h5L9 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

const labelStyle = {
  fontSize: 10,
  fontWeight: 700,
  color: '#A39992',
  textTransform: 'uppercase',
  letterSpacing: '0.09em',
}
